import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ESCUDO } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, LogOut } from "lucide-react";

/** Smoothly scroll the element with id=`hash` into view. */
function scrollToHash(hash) {
  if (!hash) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const id = hash.startsWith("#") ? hash.slice(1) : hash;
  // Retry a couple of times in case the section just mounted.
  let attempts = 0;
  const tick = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (attempts++ < 8) setTimeout(tick, 80);
  };
  tick();
}

export default function DashboardLayout({ navItems, role, children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Whenever the hash (or pathname) changes, scroll to the corresponding section.
  useEffect(() => {
    scrollToHash(location.hash);
  }, [location.pathname, location.hash]);

  const handleNavClick = useCallback(
    (e, to) => {
      const [path, hash = ""] = (to || "").split("#");
      const targetPath = path || location.pathname;
      const samePath = location.pathname === targetPath;
      const sameHash = (location.hash || "").replace("#", "") === hash;

      // Always close mobile sidebar
      setOpen(false);

      // When already on the same path+hash, React Router won't fire navigation,
      // so we manually trigger the scroll to ensure the visual jump still happens.
      if (samePath && sameHash) {
        e.preventDefault();
        scrollToHash(hash);
        return;
      }
      // Same path, different hash → navigate (router will update location.hash,
      // and the useEffect above will scroll). Let the default <Link> handle it.
    },
    [location.pathname, location.hash]
  );

  return (
    <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-[280px,1fr]" data-testid={`dashboard-${role}`}>
      {/* Sidebar */}
      <aside className={`dashboard-sidebar text-primary-foreground lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] lg:flex flex-col p-6 ${open ? "fixed inset-0 z-40 flex" : "hidden lg:flex"}`}>
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-3" data-testid="sidebar-logo">
            <img src={ESCUDO} alt="Escudo" className="w-12 h-12 rounded-full bg-white p-1" />
            <div className="leading-tight">
              <p className="font-display text-lg font-bold">Pastoral</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-secondary font-bold">Virreyista 2026</p>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden text-primary-foreground" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="rounded-2xl bg-white/10 backdrop-blur p-4 mb-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-secondary">{user?.role === "catechist" ? "Catequista" : user?.role === "admin" ? "Administrador" : "Estudiante"}</p>
          <p className="font-display text-lg mt-1 leading-tight">{user?.name}</p>
          <p className="text-xs opacity-70 mt-1 truncate">{user?.email}</p>
          {user?.group_code && (
            <p className="mt-2 inline-block text-[10px] tracking-wider uppercase bg-secondary text-secondary-foreground px-2 py-1 rounded-full font-bold">
              {user.group_code}
            </p>
          )}
        </div>

        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const [itemPath, itemHash = ""] = (item.to || "").split("#");
            const samePath = location.pathname === itemPath;
            const isOverview = !itemHash;
            const active = samePath && (isOverview ? !location.hash : `#${itemHash}` === location.hash);
            const Icon = item.icon;
            return (
              <Link
                key={item.id || item.to}
                to={item.to}
                onClick={(e) => handleNavClick(e, item.to)}
                data-testid={`sb-${item.id}`}
                aria-current={active ? "page" : undefined}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                  active ? "bg-secondary text-secondary-foreground font-semibold" : "hover:bg-white/10"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="sidebar-logout"
          className="mt-4 flex items-center gap-3 px-4 py-3 rounded-xl text-sm hover:bg-white/10 transition-all"
        >
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
      </aside>

      {/* Mobile bar */}
      <div className="lg:hidden p-4 border-b border-border bg-card flex items-center justify-between" data-testid="mobile-dashboard-bar">
        <button onClick={() => setOpen(true)} aria-label="Menú" className="text-primary">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-display text-lg text-primary">Mi Panel</span>
        <span className="w-6" />
      </div>

      {/* Main content */}
      <main className="p-6 lg:p-10 bg-background overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
